/**
 * MinistrareIA - Onboarding Chat UI
 * Container do chat com mensagens e área de input
 * 
 * UPDATED: 
 * - Scroll suavizado sem puxões bruscos
 * - Scroll só quando necessário
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageSquare, User, Sparkles } from 'lucide-react';
import {
  TextOnlyInput,
  TextAudioInput,
  OptionsInput,
  OptionsCustomInput,
  MultiselectTextInput,
  FAQCardsInput
} from './OnboardingInputs';

// ============================================
// MESSAGE COMPONENT
// ============================================

const ChatMessage = ({ message, colors, isDark }) => {
  const isBot = message.type === 'bot';

  const styles = {
    container: {
      display: 'flex',
      justifyContent: isBot ? 'flex-start' : 'flex-end',
      animation: 'messageSlide 0.3s ease-out',
    },
    wrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '10px',
      maxWidth: '80%',
      flexDirection: isBot ? 'row' : 'row-reverse',
    },
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      backgroundColor: isBot 
        ? (isDark ? colors.bgPrimary : '#ffffff')
        : colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      border: isBot ? `1px solid ${colors.borderColor}` : 'none',
    },
    bubble: {
      padding: '14px 18px',
      backgroundColor: isBot 
        ? (isDark ? colors.bgPrimary : '#ffffff')
        : colors.accentPrimary,
      color: isBot ? colors.textPrimary : 'white',
      borderRadius: isBot ? '18px 18px 18px 6px' : '18px 18px 6px 18px',
      border: isBot ? `1px solid ${colors.borderColor}` : 'none',
      boxShadow: isBot ? colors.shadowSm : 'none',
    },
    text: {
      fontSize: '15px',
      lineHeight: 1.5,
      whiteSpace: 'pre-wrap',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.avatar}>
          {isBot ? (
            <MessageSquare size={18} color={colors.accentPrimary} />
          ) : (
            <User size={18} color="white" />
          )}
        </div>
        <div style={styles.bubble}>
          <p style={styles.text}>{message.content}</p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// TYPING INDICATOR
// ============================================

const TypingIndicator = ({ colors, isDark }) => {
  const styles = {
    container: {
      display: 'flex',
      justifyContent: 'flex-start',
      animation: 'messageSlide 0.3s ease-out',
    },
    wrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '10px',
    },
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${colors.borderColor}`,
    },
    bubble: {
      padding: '16px 20px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      borderRadius: '18px 18px 18px 6px',
      border: `1px solid ${colors.borderColor}`,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: colors.textTertiary,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.avatar}>
          <MessageSquare size={18} color={colors.accentPrimary} />
        </div>
        <div style={styles.bubble}>
          <span style={{ ...styles.dot, animation: 'typingBounce 1s infinite 0ms' }} />
          <span style={{ ...styles.dot, animation: 'typingBounce 1s infinite 150ms' }} />
          <span style={{ ...styles.dot, animation: 'typingBounce 1s infinite 300ms' }} />
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN CHAT UI COMPONENT
// ============================================

const OnboardingChatUI = ({
  messages,
  isTyping,
  inputReady,
  sectionConfig,
  onOptionSelect,
  onTextSubmit,
  onSkip,
  onTranscribe,
  isProcessingAudio,
  headerTitle = 'Criando seu agente',
  headerSubtitle = 'Responda as perguntas para personalizar'
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const lastScrollRef = useRef(0);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Scroll suave para o final
  const scrollToBottom = useCallback((immediate = false) => {
    if (!messagesAreaRef.current) return;
    
    const container = messagesAreaRef.current;
    const targetScroll = container.scrollHeight - container.clientHeight;
    
    // Se já está próximo do final, não faz scroll
    const isNearBottom = container.scrollTop > targetScroll - 100;
    
    if (immediate) {
      container.scrollTop = targetScroll;
      lastScrollRef.current = targetScroll;
    } else if (!isNearBottom || targetScroll > lastScrollRef.current) {
      // Scroll suave só se não estiver no final ou se tiver novo conteúdo
      requestAnimationFrame(() => {
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
        lastScrollRef.current = targetScroll;
      });
    }
  }, []);

  // Scroll quando mensagens mudam
  useEffect(() => {
    if (messages.length > 0) {
      // Delay pequeno para garantir que o DOM atualizou
      const timer = setTimeout(() => scrollToBottom(), 100);
      return () => clearTimeout(timer);
    }
  }, [messages.length, scrollToBottom]);

  // Scroll quando typing muda
  useEffect(() => {
    if (isTyping) {
      const timer = setTimeout(() => scrollToBottom(), 50);
      return () => clearTimeout(timer);
    }
  }, [isTyping, scrollToBottom]);

  // Scroll quando input aparece
  useEffect(() => {
    if (inputReady) {
      // Delay maior para o input ter tempo de renderizar
      const timer = setTimeout(() => scrollToBottom(), 200);
      return () => clearTimeout(timer);
    }
  }, [inputReady, scrollToBottom]);

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: colors.bgSecondary,
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '16px 20px' : '20px 24px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      borderBottom: `1px solid ${colors.borderColor}`,
      flexShrink: 0,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    headerContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: '4px',
    },
    headerTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerTitle: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    headerSubtitle: {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    messagesContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    messagesArea: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: isMobile ? '20px 16px' : '28px 24px',
      paddingBottom: '24px',
      scrollBehavior: 'smooth',
    },
    messagesInner: {
      maxWidth: '700px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    spacer: {
      height: '20px',
      flexShrink: 0,
    },
    inputArea: {
      padding: isMobile ? '16px' : '20px 24px',
      paddingBottom: isMobile ? '24px' : '24px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      borderTop: `1px solid ${colors.borderColor}`,
      flexShrink: 0,
    },
    inputWrapper: {
      maxWidth: '700px',
      margin: '0 auto',
    },
  };

  // Renderiza o input apropriado baseado no tipo da seção
  const renderInput = () => {
    if (!sectionConfig) return null;

    const { inputType } = sectionConfig;

    switch (inputType) {
      case 'text_only':
        return (
          <TextOnlyInput
            onSubmit={onTextSubmit}
            onSkip={onSkip}
            placeholder={sectionConfig.placeholder}
            maxLength={sectionConfig.maxLength || 200}
            minLength={sectionConfig.minLength || 10}
            skipText={sectionConfig.skipText}
            required={sectionConfig.required}
          />
        );

      case 'text_audio':
        return (
          <TextAudioInput
            onSubmit={onTextSubmit}
            onSkip={onSkip}
            placeholder={sectionConfig.placeholder}
            minLength={sectionConfig.minLength || 10}
            skipText={sectionConfig.skipText}
            required={sectionConfig.required}
            isProcessingAudio={isProcessingAudio}
            onTranscribe={onTranscribe}
          />
        );

      case 'options':
        return (
          <OptionsInput
            options={sectionConfig.options}
            onSelect={onOptionSelect}
            onSkip={onSkip}
            skipText={sectionConfig.skipText}
            required={sectionConfig.required}
          />
        );

      case 'options_custom':
        return (
          <OptionsCustomInput
            options={sectionConfig.options}
            onSelect={onOptionSelect}
            customPlaceholder={sectionConfig.customPlaceholder}
          />
        );

      case 'multiselect_text':
        return (
          <MultiselectTextInput
            options={sectionConfig.options}
            onSubmit={(data) => onTextSubmit(data)}
            textPlaceholder={sectionConfig.textPlaceholder}
          />
        );

      case 'faq_cards':
        return (
          <FAQCardsInput
            onSubmit={(data) => onTextSubmit(data)}
            onSkip={onSkip}
            skipText={sectionConfig.skipText}
            maxItems={sectionConfig.maxItems || 15}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header - Centralizado */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerTitleRow}>
            <Sparkles size={20} color={colors.accentPrimary} />
            <span style={styles.headerTitle}>{headerTitle}</span>
          </div>
          <span style={styles.headerSubtitle}>{headerSubtitle}</span>
        </div>
      </header>

      {/* Messages Container */}
      <div style={styles.messagesContainer}>
        <div ref={messagesAreaRef} style={styles.messagesArea}>
          <div style={styles.messagesInner}>
            {messages.map((message) => (
              <ChatMessage 
                key={message.id} 
                message={message} 
                colors={colors}
                isDark={isDark}
              />
            ))}

            {isTyping && <TypingIndicator colors={colors} isDark={isDark} />}

            {/* Spacer para garantir espaço no final */}
            <div style={styles.spacer} ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input Area - Só aparece quando inputReady === true */}
      {inputReady && sectionConfig && (
        <div 
          style={{
            ...styles.inputArea,
            animation: 'inputSlideUp 0.25s ease-out',
          }}
        >
          <div style={styles.inputWrapper}>
            {renderInput()}
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes messageSlide {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes typingBounce {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-4px);
          }
        }
        @keyframes inputSlideUp {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingChatUI;